import * as React from "react";
import { watchAppliedTheme } from "../../../../shared/utility/designer-theme";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ResponsiveRowInfo } from "./responsive-design-toolkit";

declare let window: any;

/**
 * Whether the drag-to-resize bar can be drawn for this row. It needs every desktop column to carry
 * a size (and not the -1 that means "not set"), and those sizes to fit the grid in one row.
 *
 * The toolkit used to answer "no" with a paragraph of prose in a bordered box, which took more of
 * the panel than the bar it replaced and told the user nothing the column size selectors above it
 * do not already show. The section is simply absent now, so this is what decides.
 */
export function isResponsiveRowCustomizable(
  responsiveRowInfo: ResponsiveRowInfo,
  gridSystemColumnCount: number
): boolean {
  const columns = responsiveRowInfo?.desktopColumns ?? [];
  if (columns.length === 0) {
    return false;
  }
  let total: number = 0;
  for (const column of columns) {
    if (!column.value || column.value === -1) {
      return false;
    }
    total += column.value;
  }
  return total <= gridSystemColumnCount;
}

export interface IResponsiveDesignToolkitCustomizeState {}

export interface IResponsiveDesignToolkitCustomizeProps {
  responsiveRowInfo: ResponsiveRowInfo;
  gridSystemColumnCount: number;
  onColumnResized: (columnIndex: number, leftSize: number, rightSize: number) => void;
  onColumnSplitted: (columnIndex: number, leftSize: number, rightSize: number) => void;
}

interface CanvasColumn {
  x: number;
  width: number;
  value: number;
  isSelected: boolean;
}

interface CanvasSplitter {
  x: number;
  columnIndex: number;
  isHovered: boolean;
}

/** The colours the bar paints with, by role. Canvas pixels cannot hold `var()`, so these are resolved literals. */
interface CustomizePalette {
  /** The bar's own ground, and the gap drawn either side of a splitter. */
  surface: string;
  /** Outer border, selected column, split handle. */
  accent: string;
  /** Text and strokes drawn on top of `accent`. */
  onAccent: string;
  /** Unselected column and splitter. */
  column: string;
  columnText: string;
  splitterHover: string;
}

/** Light-theme values, used only when the tokens cannot be read. */
const FALLBACK_PALETTE: CustomizePalette = {
  surface: "#ffffff",
  accent: "#246dff",
  onAccent: "#ffffff",
  column: "#ccddff",
  columnText: "#41546e",
  splitterHover: "#dcdfe4"
};

/** Share of the way a hovered column moves from `column` toward `surface`, at full hover. */
const COLUMN_HOVER_MIX: number = 0.6;

const HEX_COLOUR_PATTERN: RegExp = /^#[0-9a-f]{6}$/i;

const resolveCustomizePalette = (): CustomizePalette => {
  // One computed style for every token rather than one per token.
  const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);
  const tokenOr = (token: string, fallback: string): string => {
    const resolved: string = rootStyle.getPropertyValue(token).trim();
    return resolved.length > 0 ? resolved : fallback;
  };
  return {
    surface: tokenOr("--dark-0", FALLBACK_PALETTE.surface),
    accent: tokenOr("--blue-5", FALLBACK_PALETTE.accent),
    // --white is a literal in both themes: the text sits on --blue-5, which does not flip either.
    onAccent: tokenOr("--white", FALLBACK_PALETTE.onAccent),
    column: tokenOr("--blue-2", FALLBACK_PALETTE.column),
    columnText: tokenOr("--dark-8", FALLBACK_PALETTE.columnText),
    splitterHover: tokenOr("--dark-3", FALLBACK_PALETTE.splitterHover)
  };
};

/** `from` moved `amount` (0..1) of the way to `to`. Returns `from` unchanged unless both are `#rrggbb`. */
const mixHexColours = (from: string, to: string, amount: number): string => {
  if (!HEX_COLOUR_PATTERN.test(from) || !HEX_COLOUR_PATTERN.test(to)) return from;
  const channel = (hex: string, offset: number): number => parseInt(hex.slice(offset, offset + 2), 16);
  const mixed: number[] = [1, 3, 5].map((offset: number): number =>
    Math.round(channel(from, offset) + (channel(to, offset) - channel(from, offset)) * amount)
  );
  return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
};

export default class ResponsiveDesignToolkitCustomize extends React.Component<
  IResponsiveDesignToolkitCustomizeProps,
  IResponsiveDesignToolkitCustomizeState
> {
  private canvasRef = React.createRef<HTMLCanvasElement>();

  private animationFrameId: number | null = null;

  private dpr = window.devicePixelRatio || 1;

  private static readonly CANVAS_HEIGHT = 26; // Match original height: 26px + 4px padding + 2px border

  private static readonly CANVAS_WIDTH = 216; // Match original: 216px

  private static readonly COLUMN_HEIGHT = 22; // Match original line-height

  private static readonly SPLITTER_WIDTH = 4;

  private static readonly SPLIT_ICON_SIZE = 8;

  // Resolved on mount and on each theme flip, never per draw: draw() runs every frame while dragging.
  private palette: CustomizePalette = FALLBACK_PALETTE;

  private stopWatchingTheme: (() => void) | null = null;

  private columns: CanvasColumn[] = [];

  private splitters: CanvasSplitter[] = [];

  private hoveredColumnIndex = -1;

  private hoveredSplitterIndex = -1;

  private isDragging = false;

  private isResizing = false;

  private isSplitting = false;

  private dragStartX = 0;

  private splitIconVisible = false;

  private splitIconX = 0;

  private tempColumn: CanvasColumn | null = null;

  private splitLeftColumn: CanvasColumn | null = null;

  private hoverAnimationProgress = 0;

  private targetHoverProgress = 0;

  private lastDrawTime = 0;

  private currentLeftValue = 0;

  private currentRightValue = 0;

  constructor(props: IResponsiveDesignToolkitCustomizeProps) {
    super(props);
    this.updateCanvasData();
  }

  componentDidMount() {
    this.palette = resolveCustomizePalette();
    this.stopWatchingTheme = watchAppliedTheme((): void => {
      this.palette = resolveCustomizePalette();
      this.draw();
    });
    this.setupCanvas();
    this.updateCanvasData();
    this.draw();
  }

  componentDidUpdate(prevProps: IResponsiveDesignToolkitCustomizeProps) {
    if (prevProps.responsiveRowInfo !== this.props.responsiveRowInfo) {
      this.updateCanvasData();
      this.draw();
    }
  }

  componentWillUnmount() {
    this.stopWatchingTheme?.();
    this.stopWatchingTheme = null;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.removeEventListeners();

    document.body.style.cursor = "";
  }

  private setupCanvas = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = ResponsiveDesignToolkitCustomize.CANVAS_WIDTH * this.dpr;
    canvas.height = ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT * this.dpr;
    canvas.style.width = `${ResponsiveDesignToolkitCustomize.CANVAS_WIDTH}px`;
    canvas.style.height = `${ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT}px`;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(this.dpr, this.dpr);
    }
  };

  private updateCanvasData = () => {
    const desktopColumns = this.props.responsiveRowInfo.desktopColumns;
    if (!desktopColumns) return;

    this.columns = [];
    this.splitters = [];

    const totalValue = desktopColumns.reduce((sum, col) => sum + col.value, 0);
    const availableWidth = ResponsiveDesignToolkitCustomize.CANVAS_WIDTH - 4;
    const splitterCount = desktopColumns.length - 1;
    const totalSplitterWidth = splitterCount * ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH;
    const columnAreaWidth = availableWidth - totalSplitterWidth;

    let currentX = 2; // Start with 3px padding (1px border + 2px padding)

    desktopColumns.forEach((col, index) => {
      const width = (col.value / totalValue) * columnAreaWidth;

      this.columns.push({
        x: currentX,
        width,
        value: col.value,
        isSelected: col.isSelectedColumn || false
      });

      currentX += width;

      if (index < desktopColumns.length - 1) {
        this.splitters.push({
          x: currentX,
          columnIndex: index,
          isHovered: false
        });
        currentX += ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH;
      }
    });
  };

  private draw = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, ResponsiveDesignToolkitCustomize.CANVAS_WIDTH, ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT);

    // Draw outer border matching original design
    this.drawBorder(ctx);
    this.drawColumns(ctx);
    this.drawSplitters(ctx);

    if (this.splitIconVisible) {
      this.drawSplitIcon(ctx, this.splitIconX);
    }
  };

  private throttledDraw = () => {
    const now = performance.now();
    if (now - this.lastDrawTime > 16) {
      this.lastDrawTime = now;
      this.draw();
    } else if (!this.animationFrameId) {
      this.animationFrameId = requestAnimationFrame(() => {
        this.animationFrameId = null;
        this.draw();
      });
    }
  };

  private drawBorder = (ctx: CanvasRenderingContext2D) => {
    // Draw outer border matching original kuika_responsiveCustomizeRow
    ctx.strokeStyle = this.palette.accent;
    ctx.lineWidth = 1;
    ctx.strokeRect(
      0.5,
      0.5,
      ResponsiveDesignToolkitCustomize.CANVAS_WIDTH - 1,
      ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT - 1
    );

    // Fill background
    ctx.fillStyle = this.palette.surface;
    ctx.fillRect(
      1,
      1,
      ResponsiveDesignToolkitCustomize.CANVAS_WIDTH - 2,
      ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT - 2
    );
  };

  private drawColumns = (ctx: CanvasRenderingContext2D) => {
    const y = (ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT - ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT) / 2;

    this.columns.forEach((column, index) => {
      // Skip drawing the original column if we're showing a split preview for this column
      if (index === this.hoveredColumnIndex && this.splitLeftColumn && this.tempColumn) {
        return; // Don't draw the original column, we'll draw the split version below
      }

      const isHovered = index === this.hoveredColumnIndex;
      const isSelected = column.isSelected;

      // Match original colors with smooth hover animations
      if (isSelected) {
        ctx.fillStyle = this.palette.accent;
      } else if (isHovered && this.hoverAnimationProgress > 0) {
        // Fade toward the bar's ground on hover — lighter in light, deeper in dark
        ctx.fillStyle = mixHexColours(
          this.palette.column,
          this.palette.surface,
          COLUMN_HOVER_MIX * this.hoverAnimationProgress
        );
      } else {
        ctx.fillStyle = this.palette.column;
      }
      ctx.fillRect(column.x, y, column.width, ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT);

      // Text styling to match original
      if (isSelected) {
        ctx.fillStyle = this.palette.onAccent;
        ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      } else {
        ctx.fillStyle = this.palette.columnText;
        ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      }
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        column.value.toString(),
        Math.round(column.x + column.width / 2),
        Math.round(y + ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT / 2)
      );
    });

    // Draw split preview columns if we're showing a split
    if (this.splitLeftColumn && this.tempColumn && this.hoveredColumnIndex !== -1) {
      // Draw left split column
      const leftColumn = this.splitLeftColumn;
      ctx.fillStyle = leftColumn.isSelected ? this.palette.accent : this.palette.column;
      ctx.fillRect(leftColumn.x, y, leftColumn.width, ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT);

      ctx.fillStyle = leftColumn.isSelected ? this.palette.onAccent : this.palette.columnText;
      ctx.font = leftColumn.isSelected
        ? 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        leftColumn.value.toString(),
        Math.round(leftColumn.x + leftColumn.width / 2),
        Math.round(y + ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT / 2)
      );

      // Draw right split column
      const rightColumn = this.tempColumn;
      ctx.fillStyle = this.palette.column;
      ctx.fillRect(rightColumn.x, y, rightColumn.width, ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT);

      ctx.fillStyle = this.palette.columnText;
      ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        rightColumn.value.toString(),
        Math.round(rightColumn.x + rightColumn.width / 2),
        Math.round(y + ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT / 2)
      );
    }
  };

  private drawSplitters = (ctx: CanvasRenderingContext2D) => {
    const y = (ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT - ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT) / 2;

    this.splitters.forEach((splitter, index) => {
      const isHovered = index === this.hoveredSplitterIndex;

      // Splitter in the column colour with ground-coloured gaps either side
      // Slightly darker when hovered to show it's interactive
      ctx.fillStyle = isHovered ? this.palette.splitterHover : this.palette.column;
      ctx.fillRect(
        splitter.x,
        y,
        ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH,
        ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT
      );

      // Gaps on left and right in the bar's ground colour, so the splitter reads as a separator
      ctx.strokeStyle = this.palette.surface;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(splitter.x + 0.5, y);
      ctx.lineTo(splitter.x + 0.5, y + ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT);
      ctx.moveTo(splitter.x + ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH - 0.5, y);
      ctx.lineTo(
        splitter.x + ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH - 0.5,
        y + ResponsiveDesignToolkitCustomize.COLUMN_HEIGHT
      );
      ctx.stroke();
    });
  };

  private drawSplitIcon = (ctx: CanvasRenderingContext2D, x: number) => {
    if (!this.splitIconVisible || this.hoverAnimationProgress <= 0) return;

    const centerY = ResponsiveDesignToolkitCustomize.CANVAS_HEIGHT / 2;
    const iconWidth = 8; // Match original SVG width="8"
    const iconHeight = 22; // Match original SVG height="22"
    const alpha = this.hoverAnimationProgress;

    // Determine anchor point based on split values
    // Keep the visual center of the icon consistent regardless of anchor point
    let iconX = x;
    let iconCenterX = x; // Visual center stays the same

    if (this.currentLeftValue <= 3) {
      // Anchor from left edge of icon instead of center
      iconX = x;
      iconCenterX = x + iconWidth / 2; // Visual center is right of anchor
    } else if (this.currentRightValue <= 3) {
      // Anchor from right edge of icon instead of center
      iconX = x - iconWidth;
      iconCenterX = x - iconWidth / 2; // Visual center is left of anchor
    } else {
      // Normal center anchoring
      iconX = x - iconWidth / 2;
      iconCenterX = x; // Visual center matches anchor
    }

    ctx.save();
    ctx.globalAlpha = alpha;

    // Draw the rounded background with adjusted anchor point
    ctx.fillStyle = this.palette.accent;
    ctx.beginPath();
    ctx.roundRect(iconX, centerY - iconHeight / 2, iconWidth, iconHeight, 4);
    ctx.fill();

    // Draw vertical line (main stem) - always at visual center
    ctx.strokeStyle = this.palette.onAccent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(iconCenterX, centerY - 8);
    ctx.lineTo(iconCenterX, centerY + 8);
    ctx.stroke();

    // Draw center circle - always at visual center
    ctx.fillStyle = this.palette.onAccent;
    ctx.beginPath();
    ctx.arc(iconCenterX, centerY, 3, 0, Math.PI * 2);
    ctx.fill();

    // Draw plus sign inside circle - always at visual center
    ctx.strokeStyle = this.palette.accent;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(iconCenterX - 2, centerY);
    ctx.lineTo(iconCenterX + 2, centerY);
    ctx.moveTo(iconCenterX, centerY - 2);
    ctx.lineTo(iconCenterX, centerY + 2);
    ctx.stroke();

    ctx.restore();
  };

  private getMousePosition = (event: MouseEvent): { x: number; y: number } => {
    const canvas = this.canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  private getColumnAtPosition = (x: number): number => {
    for (let i = 0; i < this.columns.length; i++) {
      const column = this.columns[i];
      if (x >= column.x && x <= column.x + column.width) {
        return i;
      }
    }
    return -1;
  };

  private getSplitterAtPosition = (x: number): number => {
    for (let i = 0; i < this.splitters.length; i++) {
      const splitter = this.splitters[i];
      // Expand hit area to 8px (2px on each side) for easier grabbing
      const hitAreaPadding = 2;
      if (
        x >= splitter.x - hitAreaPadding &&
        x <= splitter.x + ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH + hitAreaPadding
      ) {
        return i;
      }
    }
    return -1;
  };

  private removeEventListeners = () => {
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseUp);
  };

  private updateCursor = (cursor: string) => {
    const canvas = this.canvasRef.current;
    if (canvas) {
      canvas.style.cursor = cursor;
    }

    // Also set cursor on body during drag operations to ensure visibility
    if (this.isDragging) {
      document.body.style.cursor = cursor;
    }
  };

  private resetState = () => {
    this.hoveredColumnIndex = -1;
    this.hoveredSplitterIndex = -1;
    this.isDragging = false;
    this.isResizing = false;
    this.isSplitting = false;
    this.splitIconVisible = false;
    this.tempColumn = null;
    this.splitLeftColumn = null;
    this.removeEventListeners();
    KMainFunctions.hideResizeWrapper();

    // Reset body cursor when drag ends
    document.body.style.cursor = "";
  };

  private handleCanvasMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    // Prevent default drag behavior that might interfere with cursor
    event.preventDefault();
    event.stopPropagation();

    const { x } = this.getMousePosition(event.nativeEvent);

    const splitterIndex = this.getSplitterAtPosition(x);
    const columnIndex = this.getColumnAtPosition(x);

    this.dragStartX = x;

    if (splitterIndex !== -1) {
      this.isDragging = true;
      this.isResizing = true;
      this.hoveredSplitterIndex = splitterIndex;
      this.updateCursor("col-resize"); // Keep resize cursor during drag
      KMainFunctions.showResizeWrapper();
      window.addEventListener("mousemove", this.handleMouseMove);
      window.addEventListener("mouseup", this.handleMouseUp);
    } else if (columnIndex !== -1 && this.columns[columnIndex].value >= 2) {
      this.isDragging = true;
      this.isSplitting = true;
      this.hoveredColumnIndex = columnIndex;
      this.splitIconVisible = true;
      this.splitIconX = x;
      this.updateCursor("pointer"); // Keep pointer cursor during split drag
      window.addEventListener("mousemove", this.handleMouseMove);
      window.addEventListener("mouseup", this.handleMouseUp);
    }

    this.draw();
  };

  private handleCanvasMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    // Don't handle canvas mouse move if we're dragging (window mouse move takes over)
    if (this.isDragging) {
      return;
    }

    const { x } = this.getMousePosition(event.nativeEvent);
    const columnIndex = this.getColumnAtPosition(x);
    const splitterIndex = this.getSplitterAtPosition(x);

    let needsRedraw = false;

    // Check if hover states have changed
    if (columnIndex !== this.hoveredColumnIndex || splitterIndex !== this.hoveredSplitterIndex) {
      this.hoveredColumnIndex = columnIndex;
      this.hoveredSplitterIndex = splitterIndex;
      needsRedraw = true;

      // Clear split preview when not hovering over a splittable column
      this.splitLeftColumn = null;
      this.tempColumn = null;
      this.currentLeftValue = 0;
      this.currentRightValue = 0;

      if (splitterIndex !== -1) {
        // Hovering over a splitter - hide split icon
        this.splitIconVisible = false;
        this.targetHoverProgress = 0;
        this.hoverAnimationProgress = 0;
        this.updateCursor("col-resize");
      } else if (columnIndex !== -1 && this.columns[columnIndex].value >= 2) {
        // Hovering over a splittable column - show split icon
        this.splitIconVisible = true;
        this.targetHoverProgress = 1;
        this.hoverAnimationProgress = 1;
        this.updateCursor("pointer");
      } else {
        // Not hovering over anything interactive
        this.splitIconVisible = false;
        this.targetHoverProgress = 0;
        this.hoverAnimationProgress = 0;
        this.updateCursor("default");
      }
    }

    // Update split icon position and show split preview if hovering over a splittable column
    if (columnIndex !== -1 && this.columns[columnIndex].value >= 2 && splitterIndex === -1) {
      this.splitIconX = x;
      this.performSplit(x);
      needsRedraw = true;
    }

    if (needsRedraw) {
      this.throttledDraw();
    }
  };

  private handleCanvasMouseLeave = () => {
    if (!this.isDragging) {
      this.hoveredColumnIndex = -1;
      this.hoveredSplitterIndex = -1;
      this.splitIconVisible = false;
      this.targetHoverProgress = 0;
      this.hoverAnimationProgress = 0;
      this.splitLeftColumn = null;
      this.tempColumn = null;
      this.currentLeftValue = 0;
      this.currentRightValue = 0;
      this.updateCursor("default");
      this.throttledDraw();
    }
    // Don't change cursor during drag operations - let the drag handlers manage it
  };

  private startAnimation = () => {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animate();
  };

  private animate = () => {
    const diff = this.targetHoverProgress - this.hoverAnimationProgress;
    if (Math.abs(diff) > 0.01) {
      this.hoverAnimationProgress += diff * 0.15; // Smooth easing
      this.draw();
      this.animationFrameId = requestAnimationFrame(this.animate);
    } else {
      this.hoverAnimationProgress = this.targetHoverProgress;
      this.draw();
      this.animationFrameId = null;
    }
  };

  private handleMouseMove = (event: MouseEvent) => {
    // Prevent any default drag behaviors that might interfere
    event.preventDefault();
    event.stopPropagation();

    const { x } = this.getMousePosition(event);

    if (this.isResizing && this.hoveredSplitterIndex !== -1) {
      // Ensure cursor stays as resize during resize operations
      this.updateCursor("col-resize");
      this.performResize(x);
      this.throttledDraw();
    } else if (this.isSplitting && this.hoveredColumnIndex !== -1) {
      // Ensure cursor stays as pointer during split operations
      this.updateCursor("pointer");
      this.performSplit(x);
      this.throttledDraw();
    }
  };

  private handleMouseUp = (event: MouseEvent) => {
    if (this.isResizing && this.hoveredSplitterIndex !== -1) {
      this.finishResize();
    } else if (this.isSplitting && this.hoveredColumnIndex !== -1) {
      this.finishSplit();
    }

    // Reset cursor after drag ends
    this.updateCursor("default");
    this.resetState();
    this.draw();
  };

  private performResize = (mouseX: number) => {
    const splitterIndex = this.hoveredSplitterIndex;
    const leftColumn = this.columns[splitterIndex];
    const rightColumn = this.columns[splitterIndex + 1];

    if (!leftColumn || !rightColumn) return;

    const totalValue = leftColumn.value + rightColumn.value;
    const totalWidth = leftColumn.width + rightColumn.width;
    const leftEdge = leftColumn.x;

    let newLeftWidth = Math.max(10, mouseX - leftEdge);
    newLeftWidth = Math.min(newLeftWidth, totalWidth - 10);

    const newRightWidth = totalWidth - newLeftWidth;

    const newLeftValue = Math.max(1, Math.round((newLeftWidth / totalWidth) * totalValue));
    const newRightValue = Math.max(1, totalValue - newLeftValue);

    leftColumn.width = (newLeftValue / totalValue) * totalWidth;
    rightColumn.width = (newRightValue / totalValue) * totalWidth;
    rightColumn.x = leftColumn.x + leftColumn.width + ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH;
    leftColumn.value = newLeftValue;
    rightColumn.value = newRightValue;

    this.splitters[splitterIndex].x = leftColumn.x + leftColumn.width;

    this.updateSubsequentPositions(splitterIndex + 1);
  };

  private performSplit = (mouseX: number) => {
    const columnIndex = this.hoveredColumnIndex;
    const column = this.columns[columnIndex];

    if (!column || column.value < 2) return;

    const relativeX = mouseX - column.x;
    const splitRatio = Math.max(0, Math.min(1, relativeX / column.width));

    // Calculate left value first, ensuring it's at least 1
    let newLeftValue = Math.max(1, Math.round(column.value * splitRatio));

    // Calculate right value, ensuring it's at least 1
    let newRightValue = column.value - newLeftValue;

    // Ensure both sides have at least 1, adjusting if necessary
    if (newRightValue < 1) {
      newRightValue = 1;
      newLeftValue = column.value - 1;
    }
    if (newLeftValue < 1) {
      newLeftValue = 1;
      newRightValue = column.value - 1;
    }

    // Create temporary split visualization without modifying original data
    const totalValue = column.value;

    // Calculate proportional widths
    const leftWidth = (newLeftValue / totalValue) * column.width;
    const rightWidth = (newRightValue / totalValue) * column.width;

    // Create temporary left column for split preview
    this.splitLeftColumn = {
      x: column.x,
      width: leftWidth,
      value: newLeftValue,
      isSelected: column.isSelected
    };

    // Create temporary right column for split preview
    this.tempColumn = {
      x: column.x + leftWidth,
      width: rightWidth,
      value: newRightValue,
      isSelected: false
    };

    // Calculate the natural split position (where the split boundary is)
    const naturalSplitX = column.x + leftWidth;

    // Store current split values for anchor point calculation in drawing
    this.currentLeftValue = newLeftValue;
    this.currentRightValue = newRightValue;

    // The split icon position is at the natural split boundary
    this.splitIconX = naturalSplitX;
  };

  private updateSubsequentPositions = (startIndex: number) => {
    for (let i = startIndex; i < this.columns.length; i++) {
      if (i === 0) {
        this.columns[i].x = 4;
      } else {
        this.columns[i].x =
          this.columns[i - 1].x + this.columns[i - 1].width + ResponsiveDesignToolkitCustomize.SPLITTER_WIDTH;
      }

      if (i < this.splitters.length) {
        this.splitters[i].x = this.columns[i].x + this.columns[i].width;
      }
    }
  };

  private finishResize = () => {
    const splitterIndex = this.hoveredSplitterIndex;
    const leftColumn = this.columns[splitterIndex];
    const rightColumn = this.columns[splitterIndex + 1];

    if (leftColumn && rightColumn) {
      this.props.onColumnResized(splitterIndex, leftColumn.value, rightColumn.value);
    }
  };

  private finishSplit = () => {
    if (!this.splitLeftColumn || !this.tempColumn || this.hoveredColumnIndex === -1) return;

    const columnIndex = this.hoveredColumnIndex;
    const newLeftValue = this.splitLeftColumn.value;
    const newRightValue = this.tempColumn.value;

    this.props.onColumnSplitted(columnIndex, newLeftValue, newRightValue);
  };

  render() {
    // The parent hides the whole section when the bar cannot be drawn; this guards the canvas
    // against a render the parent has not gated yet.
    if (!isResponsiveRowCustomizable(this.props.responsiveRowInfo, this.props.gridSystemColumnCount)) {
      return null;
    }

    return (
      <div className="kuika_responsiveCustomizeParentRow">
        <canvas
          ref={this.canvasRef}
          onMouseDown={this.handleCanvasMouseDown}
          onMouseMove={this.handleCanvasMouseMove}
          onMouseLeave={this.handleCanvasMouseLeave}
          onDragStart={(e) => e.preventDefault()} // Prevent HTML5 drag behavior
          style={{
            display: "block",
            margin: "0 auto",
            maxWidth: "100%"
          }}
        />
      </div>
    );
  }
}
